/**
 * Pure resolver for the queued SRC-20 amount reveal.
 *
 * Single source of truth for the reveal decision: given all signals it returns
 * the raw amount + the token's real decimals (for the shared `TokenAmount`
 * renderer), or `null` to mean "stay masked". It never yields a wrong number.
 * It masks when ANY of these hold:
 *  - the connected wallet is not a Safe owner,
 *  - the transaction is not queued (executed/historical stay masked),
 *  - the calldata can't be decoded as an SRC-20 transfer (wrong selector / malformed),
 *  - the decoded recipient doesn't match the recipient being displayed,
 *  - the token's real decimals are unknown.
 *
 * The amount is NOT confidential: it is already publicly readable from the
 * gateway's raw `hexData`. This is a convenience reveal, not a privacy mechanism.
 */
import type { Hex } from 'viem'
import { sameAddress } from '@safe-global/utils/utils/addresses'
import { decodeSrc20TransferData } from './buildShieldedTransfer'

export interface RevealedSrc20Amount {
  /** Raw token-base-units amount, decimal string — handed to `TokenAmount`. */
  value: string
  /** The token's real decimals, for `TokenAmount` to format against. */
  decimals: number
}

export interface ResolveRevealedSrc20AmountArgs {
  /** Raw transaction calldata from `TransactionDetails.txData.hexData`. */
  hexData: Hex | string | undefined | null
  /** Token's real decimals from the SRC-20 token list — NOT the CGW's `0`. */
  decimals: number | undefined | null
  /** Connected wallet is an owner of this Safe on a Seismic chain. */
  isOwner: boolean
  /** Transaction is queued (pre-execution). Executed/historical transfers stay masked. */
  isQueued: boolean
  /** Recipient shown in the transfer row; the decoded recipient must match it. */
  expectedRecipient: string | undefined
}

export const resolveRevealedSrc20Amount = ({
  hexData,
  decimals,
  isOwner,
  isQueued,
  expectedRecipient,
}: ResolveRevealedSrc20AmountArgs): RevealedSrc20Amount | null => {
  if (!isOwner) return null
  if (!isQueued) return null
  if (decimals == null) return null

  const decoded = decodeSrc20TransferData((hexData ?? undefined) as Hex | undefined)
  if (!decoded) return null

  // Guard against a details payload whose calldata diverges from the displayed
  // recipient — never show an amount next to a recipient it wasn't sent to.
  if (!sameAddress(decoded.recipient, expectedRecipient)) return null

  return { value: decoded.amount.toString(), decimals }
}
